import { Logo } from "@/components/Logo";
import { Seo, SITE_URL } from "@/components/Seo";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, HardHat, Building2, Home, Wind, Zap, Droplets, Server,
} from "lucide-react";

const INDUSTRIES = {
  general_contracting: {
    icon: HardHat, name: "General Contracting",
    h1: "The field-service & accounting platform built for General Contractors.",
    sub: "Run multi-trade projects, subs, draws and change orders from bid to reconciled invoice - with live job margins and QuickBooks/Xero sync.",
    points: ["Multi-trade project templates with cost codes & divisions", "Live job-costing: labor, materials & subcontractor margin before you invoice", "Signed change orders and draw schedules customers approve online", "Two-way QuickBooks Online & Xero invoice sync"],
  },
  commercial_construction: {
    icon: Building2, name: "Commercial Construction",
    h1: "Purpose-built for Commercial Construction & tenant-improvement builds.",
    sub: "Coordinate ground-up and TI projects with RFIs, submittals, multi-crew scheduling and airtight cost control.",
    points: ["CSI-division cost codes and submittal/RFI references", "Real-time crew status board across every active site", "Bonded & general-conditions cost tracking with live margin", "Immutable PDF job binders for every closeout"],
  },
  residential_construction: {
    icon: Home, name: "Residential Construction",
    h1: "Custom homes and remodels, priced right and delivered on margin.",
    sub: "Manage allowances, client selections and draws while every dollar of labor and materials rolls into a live profitability view.",
    points: ["Allowance budgets and client-selection tracking", "Branded proposals homeowners approve and pay online", "Pricebook-driven estimates that stay margin-safe", "Deposit & milestone collection via Stripe"],
  },
  hvac: {
    icon: Wind, name: "HVAC",
    h1: "The all-in-one platform for commercial & residential HVAC contractors.",
    sub: "Install, service and PM agreements with equipment tracking, refrigerant logs and live job margins the office can trust.",
    points: ["Unit serial/model & refrigerant capture in the field", "Offline-first service calls with photos and signatures", "Live margin on every install before it locks to accounting", "Card & ACH payments collected on site"],
  },
  electrical: {
    icon: Zap, name: "Electrical",
    h1: "Field ops & job-costing software for Electrical contractors.",
    sub: "From service calls to tenant improvements - panel data, permits and code compliance with profitability built in.",
    points: ["Panel/gear IDs, service size and permit tracking", "OSHA pre-task lockout gate before work starts", "Live labor + materials margin per job", "QuickBooks / Xero two-way sync"],
  },
  plumbing: {
    icon: Droplets, name: "Plumbing",
    h1: "Run a tighter Plumbing operation, service to commercial.",
    sub: "Repipes, fixtures and permits with fast field capture, online approvals and real-time margin control.",
    points: ["Fixture/model capture and permit references", "Pressure-test & leak-free checklists per job", "Pricebook materials with live margin", "Deposits and invoices settled online"],
  },
  specialized_subs: {
    icon: Server, name: "Specialized Sub-trades",
    h1: "Built for Low-Voltage, Security, Fire/Life-Safety, Networking & Solar specialists.",
    sub: "Commission complex scopes with device tracking, programming labor and margins that hold up on every project.",
    points: ["Blueprint pin-and-run takeoffs with clash detection", "Device counts, serials and commissioning references", "Programming & equipment cost tags with live margin", "Branded quotes, e-sign and online payment"],
  },
};

export default function Industries() {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];
  if (!data) return <Navigate to="/" replace />;
  const Icon = data.icon;
  const seoTitle = `${data.name} Field Service & Job-Costing Software | SiteControlHQ`;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Service", name: `SiteControlHQ for ${data.name}`,
      serviceType: `${data.name} field service management software`,
      provider: { "@type": "Organization", name: "SiteControlHQ", url: SITE_URL },
      areaServed: "US", description: data.sub },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: data.name, item: `${SITE_URL}/industries/${slug}` },
    ] },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200" data-testid={`industry-page-${slug}`}>
      <Seo title={seoTitle} description={data.sub} path={`/industries/${slug}`} jsonLd={jsonLd} />
      <header className="fixed inset-x-0 top-0 z-50 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo />
          </Link>
          <Link to="/login?mode=signup" className="btn-amber rounded-full px-5 py-2 text-sm">Start free</Link>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-5 pt-36 pb-20">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-amber-500/10 text-amber-400"><Icon className="h-7 w-7" /></div>
        <p className="mt-6 font-mono-tac text-xs uppercase tracking-widest text-cyan-400">SiteControlHQ · {data.name}</p>
        <h1 className="mt-3 font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl">{data.h1}</h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{data.sub}</p>
        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/login?mode=signup" data-testid="industry-cta-signup" className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">Start free <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/#pricing" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-7 py-3.5 text-sm text-slate-200 hover:border-slate-500">View pricing</Link>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {data.points.map((p) => (
            <div key={p} className="card-tac flex items-start gap-3 p-5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              <span className="text-sm text-slate-200">{p}</span>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          {Object.entries(INDUSTRIES).filter(([s]) => s !== slug).map(([s, v]) => (
            <Link key={s} to={`/industries/${s}`} className="rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-300 hover:border-cyan-500 hover:text-white">{v.name}</Link>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800 bg-[#0d1420]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-10">
          <span className="font-display font-bold text-white">SiteControl<span className="text-[#2E7BE4]">HQ</span></span>
          <Link to="/login?mode=signup" className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm">Create workspace <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </footer>
    </div>
  );
}
